import "../assets/stylesheets/church_center_web.scss"

import "url-search-params-polyfill"
import "whatwg-fetch"

// Initializes Sentry as a side effect; must be imported before app modules so
// errors during boot are captured.
import "source/Sentry"

import * as React from "react"
import { captureException } from "@sentry/react-router"
import { hydrateRoot } from "react-dom/client"
import { HydratedRouter } from "react-router/dom"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import svg4everybody from "@planningcenter/icons/js/svg4everybody.js"
import { GLOBAL_API_VERSION } from "source/shared/api/constants"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { sentryRouterOnError } from "source/sentryRouterOnError"
import { registerVitePreloadErrorListener } from "./vitePreloadErrorListener"
import { getSubdomainFromUrl } from "source/shared/utils/getSubdomainFromUrl"
import { MISSING_ORG_URL } from "source/shared/missingOrganizationUrl"
import { SETUP_URL } from "source/shared/setupUrl"

// Must run first, before any lazy-loaded chunk/CSS can fail to preload.
registerVitePreloadErrorListener()

svg4everybody()

// NOTE: Every product team needs to sign off on a change to this version.
//
// In most cases we'll want to use per-request configuration, not this configuration.
// If we find we're doing that a lot, we can explore tooling to generate derivative api clients with overridden configuration.
sessionApiClient.setGlobalConfiguration({ version: GLOBAL_API_VERSION })

Object.assign(window, { sessionApiClient })

declare global {
  interface Window {
    serverRenderedProps?: ServerRenderedProps
  }
}

// Routes that will be rendered outside the RootLayout.
const LAYOUT_LESS_ROUTES = ["/setup", "/missing_organization"]

async function fetchServerRenderedProps(): Promise<
  ServerRenderedProps | undefined
> {
  const response = await fetch("/web_boot_props", { credentials: "include" })

  if (response.status === 404) {
    if (window.location.pathname !== "/missing_organization") {
      window.location.href = MISSING_ORG_URL
    }
    return
  } else if (!response.ok) {
    throw new Error(
      `Failed to fetch web boot props (${response.status} ${response.statusText})`,
    )
  }

  return await response.json()
}

// consider removing this hack when we fold cc-api-client into this codebase
function loadCSRFTokenToMetaTag(props: ServerRenderedProps | undefined) {
  if (!props) return

  const csrfToken = props.configuration?.csrfToken
  if (!csrfToken)
    throw new Error("CSRF token not found in server rendered props")

  let metaTag = document.querySelector('meta[name="csrf-token"]')
  if (!metaTag) {
    metaTag = document.createElement("meta")
    metaTag.setAttribute("name", "csrf-token")
    document.head.appendChild(metaTag)
  }
  if (!metaTag.getAttribute("content"))
    metaTag.setAttribute("content", csrfToken)

  return props
}

function hydrateApp(props?: ServerRenderedProps) {
  window.serverRenderedProps = props

  hydrateRoot(
    document,
    <React.StrictMode>
      <HydratedRouter onError={sentryRouterOnError} />
    </React.StrictMode>,
  )
}

function handleError(error: unknown) {
  captureException(error, {
    tags: { errorBoundary: "handleError in entry.client.tsx" },
  })

  console.error("Error booting the application:", error)
  const errorMessage = document.createElement("div")
  errorMessage.style.margin = "4rem auto"
  errorMessage.style.maxWidth = "600px"
  errorMessage.style.padding = "1rem"
  errorMessage.innerHTML = `
    <h1>Application Error</h1>
    <p>There was an error loading Church Center. Please try again later.</p>
  `

  const loadingIndicator = document.getElementsByClassName(
    "hydrate-fallback-loading",
  )[0]
  if (loadingIndicator) {
    loadingIndicator.replaceWith(errorMessage)
  } else {
    document.body.appendChild(errorMessage)
  }
}

function orgSubdomainPresent() {
  const currentHref = window.location.href

  const subdomain = getSubdomainFromUrl(currentHref)

  return !!subdomain
}

if (document.querySelector('meta[name="csrf-token"]')) {
  hydrateApp(window.serverRenderedProps!)
} else if (
  !orgSubdomainPresent() &&
  LAYOUT_LESS_ROUTES.includes(window.location.pathname)
) {
  hydrateApp()
} else if (!orgSubdomainPresent()) {
  // Redirect all subdomain-less requests to setup, client-side.
  // The SPA equivalent to what SSR does in middleware.
  // Can be cleaned up after fully enabling SSR.
  window.location.href = SETUP_URL
} else {
  fetchServerRenderedProps()
    .then(loadCSRFTokenToMetaTag)
    .then(hydrateApp)
    .catch(handleError)
}
